import { Palette, Ruler, Type } from "lucide-react";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useEditorStore } from "@/store/editorStore";
import {
  type ColorVariable,
  type FontFamilyVariable,
  type SizeVariable,
  useVariablesStore,
} from "@/store/variablesStore";
import { toCSSVarReference } from "@/utils/cssVariables";

// Custom layered document/component icon
const LayeredDocumentIcon = ({
  size = 16,
  className = "",
}: {
  size?: number;
  className?: string;
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <title>Color Variable Icon</title>
    {/* Left tilted rectangle */}
    <rect x="2" y="4" width="12" height="16" rx="1" transform="rotate(-5 8 12)" />
    {/* Right vertical rectangle with rounded corners */}
    <rect x="6" y="2" width="12" height="18" rx="2" />
    {/* Small dot/plus in bottom center of right rectangle */}
    <circle cx="12" cy="16" r="1.5" />
  </svg>
);

interface VariableSelectorProps {
  type: "color" | "size" | "fontFamily";
  onSelectVariable: (value: string) => void;
  className?: string;
}

export const VariableSelector = ({
  type,
  onSelectVariable,
  className = "",
}: VariableSelectorProps) => {
  const { projectId } = useParams<{ projectId: string }>();
  const { project } = useEditorStore();
  const { getCombinedVariables, getProjectVariables, getCraftspaceVariables } = useVariablesStore();
  const [open, setOpen] = useState(false);
  const hideVariableSelector = true;

  if (hideVariableSelector) return null;
  if (!projectId || !project?.craftspace_id) return null;

  // Get combined variables (craftspace + project)
  const _combinedVariables = getCombinedVariables(project.craftspace_id, projectId);

  // Separate craftspace and project variables
  const projectVariables = getProjectVariables(projectId);
  const craftspaceVariables = getCraftspaceVariables(project.craftspace_id);

  let projectRelevantVariables: (ColorVariable | SizeVariable | FontFamilyVariable)[] = [];
  let craftspaceRelevantVariables: (ColorVariable | SizeVariable | FontFamilyVariable)[] = [];
  let IconComponent: React.ComponentType<{ size?: number; className?: string }> =
    LayeredDocumentIcon;
  let iconColor = "text-purple-400";

  switch (type) {
    case "color":
      projectRelevantVariables = projectVariables.colors;
      craftspaceRelevantVariables = craftspaceVariables.colors.filter(
        (wsColor) => !projectVariables.colors.some((projColor) => projColor.name === wsColor.name),
      );
      IconComponent = Palette;
      iconColor = "text-blue-400";
      break;
    case "size":
      projectRelevantVariables = projectVariables.sizes;
      craftspaceRelevantVariables = craftspaceVariables.sizes.filter(
        (wsSize) => !projectVariables.sizes.some((projSize) => projSize.name === wsSize.name),
      );
      IconComponent = Ruler;
      iconColor = "text-green-400";
      break;
    case "fontFamily":
      projectRelevantVariables = projectVariables.fontFamilies;
      craftspaceRelevantVariables = craftspaceVariables.fontFamilies.filter(
        (wsFont) =>
          !projectVariables.fontFamilies.some((projFont) => projFont.name === wsFont.name),
      );
      IconComponent = Type;
      iconColor = "text-yellow-400";
      break;
  }

  const allRelevantVariables = [...craftspaceRelevantVariables, ...projectRelevantVariables];

  if (allRelevantVariables.length === 0) {
    return null; // Don't show selector if no variables available
  }

  const Icon = IconComponent;

  const handleSelectVariable = (variable: ColorVariable | SizeVariable | FontFamilyVariable) => {
    // Use CSS variable syntax instead of direct value
    onSelectVariable(toCSSVarReference(variable.name));
    setOpen(false);
  };

  // Helper function to render color swatch with transparency pattern
  const renderColorSwatch = (
    color: string,
    className: string = "w-3 h-3 rounded border border-gray-500 flex-shrink-0",
  ) => {
    // Check for transparent colors (including rgba with alpha 0)
    const isTransparent =
      color.toLowerCase() === "transparent" ||
      color === "rgba(0,0,0,0)" ||
      color === "rgba(0, 0, 0, 0)" ||
      color === "hsla(0,0%,0%,0)" ||
      color === "hsla(0, 0%, 0%, 0)";

    if (isTransparent) {
      return (
        <div
          className={className}
          style={{
            backgroundImage: `
              linear-gradient(45deg, #e5e5e5 25%, transparent 25%),
              linear-gradient(-45deg, #e5e5e5 25%, transparent 25%),
              linear-gradient(45deg, transparent 75%, #e5e5e5 75%),
              linear-gradient(-45deg, transparent 75%, #e5e5e5 75%)
            `,
            backgroundSize: "4px 4px",
            backgroundPosition: "0 0, 0 2px, 2px -2px, -2px 0px",
            backgroundColor: "#ffffff",
          }}
        />
      );
    }

    return <div className={className} style={{ backgroundColor: color }} />;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={`h-6 w-6 p-0 bg-purple-600 hover:bg-purple-700 rounded-full ${className}`}
          title={`Select ${type} variable`}
        >
          <Icon size={12} className="text-white" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3 bg-gray-800 border-gray-700" align="end" side="bottom">
        <div className="space-y-2">
          <div className="flex items-center space-x-2 pb-2 border-b border-gray-700">
            <Icon size={14} className={iconColor} />
            <span className="text-sm font-medium text-white capitalize">
              {type === "fontFamily" ? "Font Family" : type} Variables
            </span>
          </div>

          {allRelevantVariables.length === 0 ? (
            <p className="text-xs text-gray-400">No variables available</p>
          ) : (
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {/* Craftspace Variables */}
              {craftspaceRelevantVariables.length > 0 && (
                <>
                  <div className="text-xs text-gray-400 font-medium px-1 py-1 border-b border-gray-700">
                    Craftspace
                  </div>
                  {craftspaceRelevantVariables.map((variable) => (
                    <button
                      type="button"
                      key={`ws-${variable.id}`}
                      onClick={() => handleSelectVariable(variable)}
                      className="w-full flex items-center justify-between p-2 rounded bg-gray-700 hover:bg-gray-600 text-left"
                    >
                      <div className="flex items-center space-x-2 min-w-0 flex-1">
                        {type === "color" && renderColorSwatch(variable.value)}
                        <span className="text-xs font-medium text-white truncate">
                          {variable.name}
                        </span>
                      </div>
                      <span className="text-xs text-gray-400 ml-2 flex-shrink-0">
                        {type === "fontFamily" ? (
                          <span style={{ fontFamily: variable.value }}>Aa</span>
                        ) : (
                          variable.value
                        )}
                      </span>
                    </button>
                  ))}
                </>
              )}

              {/* Project Variables */}
              {projectRelevantVariables.length > 0 && (
                <>
                  {craftspaceRelevantVariables.length > 0 && (
                    <div className="text-xs text-gray-400 font-medium px-1 py-1 border-b border-gray-700 mt-2">
                      Project
                    </div>
                  )}
                  {projectRelevantVariables.map((variable) => (
                    <button
                      type="button"
                      key={variable.id}
                      onClick={() => handleSelectVariable(variable)}
                      className="w-full flex items-center justify-between p-2 rounded bg-gray-700 hover:bg-gray-600 text-left"
                    >
                      <div className="flex items-center space-x-2 min-w-0 flex-1">
                        {type === "color" && renderColorSwatch(variable.value)}
                        <span className="text-xs font-medium text-white truncate">
                          {variable.name}
                        </span>
                      </div>
                      <span className="text-xs text-gray-400 ml-2 flex-shrink-0">
                        {type === "fontFamily" ? (
                          <span style={{ fontFamily: variable.value }}>Aa</span>
                        ) : (
                          variable.value
                        )}
                      </span>
                    </button>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
